import { Component, computed, EventEmitter, inject, Input, OnInit, Output, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { firstValueFrom } from "rxjs";
import { RouterLink } from "@angular/router";
import {
  AvailabilityEntry,
  AvailabilityResult,
  LedgerEvent,
  OperationRequirement,
  OperationRequirementService,
} from "../../../_services/operation-requirement.service";
import { ArticleService } from "../../../_services";
import { ArticleTemplate } from "../../../_models";

/**
 * Auftragsbedarf: Material, das der Techniker beim Auftrag VOR ORT verbraucht.
 * Daraus entsteht kein Versand — stattdessen prüft die Verfügbarkeitsprognose,
 * ob der Auftragstechniker das Material zum Auftragstermin voraussichtlich im
 * Lager hat, und die Fehlmenge kann per Knopfdruck als Versandartikel
 * übernommen werden.
 *
 * Die Prognose kommt aus einem eigenen Endpoint und wird NACH der Bedarfsliste
 * geladen (sie kostet mehrere gruppierte Queries) — die Liste steht also sofort,
 * die Ampel kommt nach. Wie sie zustande kommt, zeigt die Berechnungstabelle:
 * je Artikelvorlage ein Kontoauszug vom heutigen Bestand bis zum Auftragstermin.
 */
@Component({
  selector: "app-operation-material-need",
  standalone: true,
  imports: [FormsModule, RouterLink],
  templateUrl: "./operation-material-need.component.html",
})
export class OperationMaterialNeedComponent implements OnInit {
  @Input({ required: true }) operationId!: number;
  /** Name des Auftragstechnikers — nur für die Kopfzeile. */
  @Input() operationTechnicianName?: string;
  /** Der Bedarf hat Versandartikel erzeugt → Versandmatrix neu laden lassen. */
  @Output() versandChanged = new EventEmitter<void>();

  private reqService = inject(OperationRequirementService);
  private articleService = inject(ArticleService);

  protected requirements = signal<OperationRequirement[]>([]);
  protected availability = signal<AvailabilityResult | null>(null);
  protected isLoading = signal<boolean>(true);
  protected isCheckingAvailability = signal<boolean>(false);
  protected isAdopting = signal<boolean>(false);

  // Prognose je Bedarfszeile, gekeyt auf die Requirement-ID.
  private entriesById = computed(() => {
    const map = new Map<number, AvailabilityEntry>();
    for (const entry of this.availability()?.entries ?? []) {
      map.set(entry.operation_requirement_id, entry);
    }
    return map;
  });

  protected entryFor(req: OperationRequirement): AvailabilityEntry | undefined {
    return this.entriesById().get(req.id);
  }

  /** Zeilen mit echter Fehlmenge — Grundlage für den Sammel-Knopf. */
  protected missingEntries = computed(() =>
    (this.availability()?.entries ?? []).filter((e) => e.status === "missing"),
  );

  protected lateEntries = computed(() =>
    (this.availability()?.entries ?? []).filter((e) => e.status === "late"),
  );

  protected missingTotal = computed(() =>
    this.missingEntries().reduce((sum, e) => sum + (e.missing ?? 0), 0),
  );

  // --- Berechnungstabelle ---

  protected showLedger = signal<boolean>(false);
  /** Artikelvorlage des offenen Tabs. */
  protected ledgerTemplateId = signal<number | null>(null);

  /**
   * Tabs der Berechnungstabelle: eine je Artikelvorlage. Freitextzeilen bleiben
   * aussen vor — ohne Artikelvorlage gibt es keinen Bestand und damit nichts zu
   * rechnen.
   */
  protected ledgerTabs = computed(() =>
    (this.availability()?.entries ?? []).filter((e) => e.article_template_id && e.detail),
  );

  protected activeLedgerEntry = computed(() => {
    const tabs = this.ledgerTabs();
    return tabs.find((e) => e.article_template_id === this.ledgerTemplateId()) ?? tabs[0];
  });

  /**
   * Auch ohne Ereignisse anbieten: dass es KEINE Zu- und Abgaenge gibt und
   * allein der Bestand zaehlt, ist selbst eine Auskunft — und ohne Knopf waere
   * die Zeile ueber den Tab zwar erreichbar, aber nicht auffindbar.
   */
  /**
   * Traegt das Ticket eine Versand-Prioritaet? Dann rechnet das Backend keine
   * Verfuegbarkeit — der Hinweis oben im Block macht das sichtbar, damit
   * niemand den Fehler woanders sucht.
   */
  protected isShippingPriority = computed(() => this.availability()?.material_shipping_order === true);

  protected hasLedger(entry?: AvailabilityEntry): boolean {
    return !!entry?.detail;
  }

  protected openLedger(entry: AvailabilityEntry): void {
    this.ledgerTemplateId.set(entry.article_template_id ?? null);
    this.showLedger.set(true);
  }

  protected closeLedger(): void {
    this.showLedger.set(false);
  }

  /** Startbestand des offenen Tabs — Ausgangszeile des Kontoauszugs. */
  protected ledgerStart(): number {
    return this.activeLedgerEntry()?.detail?.stock_now ?? 0;
  }

  /** Beschreibt ein Ereignis in einem Satz. */
  protected eventLabel(event: LedgerEvent): string {
    if (event.kind === "need") return "Bedarf eines früheren Auftrags";
    if (event.source === "planned") {
      return event.channel === "staging" ? "Staging, noch nicht versandt" : "Versand, noch nicht losgeschickt";
    }
    return event.arrived ? "Angekommen, noch nicht eingebucht" : "Unterwegs";
  }

  /** Warum dieser Ankunftstermin gilt — macht die Schaetzung nachvollziehbar. */
  protected arrivalSourceLabel(event: LedgerEvent): string {
    switch (event.arrival_source) {
      case "pickup_done":
        return "Abholauftrag erledigt";
      case "pickup_open":
        return "Termin des Abholauftrags";
      case "tracking":
        return "laut Tracking zugestellt";
      case "estimate":
        return "geschätzte Laufzeit";
      default:
        return "";
    }
  }

  /**
   * Bei einer noch offenen Sendung ist das Datum der GEPLANTE Versandtag, nicht
   * der tatsaechliche — "versandt am" waere dort schlicht falsch.
   */
  protected shippedDateLabel(event: LedgerEvent): string {
    return event.source === "planned" ? "geplanter Versand" : "versandt am";
  }

  /** Geplanter Versandtag liegt in der Vergangenheit — die Sendung haengt. */
  protected isOverdue(event: LedgerEvent): boolean {
    if (event.source !== "planned" || !event.shipped_date) return false;
    const geplant = new Date(event.shipped_date);
    const heute = new Date();
    heute.setHours(0, 0, 0, 0);
    return geplant < heute;
  }

  protected formatDate(value?: string | null): string {
    return value ? new Date(value).toLocaleDateString("de-DE") : "—";
  }

  // --- Formular ---
  protected showModal = signal<boolean>(false);
  protected editingRequirement = signal<OperationRequirement | null>(null);
  protected allTemplates = signal<ArticleTemplate[]>([]);
  protected searchResults = signal<ArticleTemplate[]>([]);
  protected selectedTemplate = signal<ArticleTemplate | null>(null);
  protected formQuantity = 1;
  protected formSearchText = "";

  private searchDebounce: ReturnType<typeof setTimeout> | null = null;

  async ngOnInit(): Promise<void> {
    await this.reload();
    void this.loadTemplates();
  }

  /** Bedarfszeilen laden; die Prognose laeuft danach im Hintergrund nach. */
  protected async reload(): Promise<void> {
    this.isLoading.set(true);
    try {
      const all = await this.reqService.loadRequirements(this.operationId);
      this.requirements.set(all.filter((r) => r.action_type === "BEDARF"));
    } finally {
      this.isLoading.set(false);
    }
    void this.refreshAvailability();
  }

  /**
   * Laeuft bewusst auch ohne Bedarfszeilen: das Ergebnis traegt die
   * Versand-Prioritaet, und ohne die wuesste die Seite nicht, dass das Anlegen
   * hier gar nicht vorgesehen ist. Ohne Zeilen ist die Abfrage billig — das
   * Backend rechnet nur je vorhandener Zeile.
   */
  protected async refreshAvailability(): Promise<void> {
    this.isCheckingAvailability.set(true);
    try {
      this.availability.set(await this.reqService.loadAvailability(this.operationId));
    } finally {
      this.isCheckingAvailability.set(false);
    }
  }

  /** Fehlmengen als Versandartikel übernehmen (Backend deckelt auf die
   *  aktuelle Fehlmenge — ein Doppelklick legt nichts doppelt an). */
  protected async adoptMissing(entry?: AvailabilityEntry): Promise<void> {
    const entries = entry?.article_template_id
      ? [{ article_template_id: entry.article_template_id, quantity: entry.missing ?? 0 }]
      : undefined;

    this.isAdopting.set(true);
    try {
      const result = await this.reqService.adoptMissing(this.operationId, entries);
      if (!result) return;
      this.availability.set(result.availability);
      this.versandChanged.emit();
    } finally {
      this.isAdopting.set(false);
    }
  }

  // --- Anzeige-Helfer ---

  protected statusLabel(entry?: AvailabilityEntry): string {
    if (!entry) return "";
    switch (entry.status) {
      case "covered":
        return "voraussichtlich vorhanden";
      case "late":
        return `Paket kommt voraussichtlich zu spät (${entry.incoming_late} unterwegs)`;
      case "missing":
        return `${entry.missing} fehlen`;
      default:
        return this.unknownLabel(entry);
    }
  }

  private unknownLabel(entry: AvailabilityEntry): string {
    switch (entry.unknown_reason) {
      case "freitext":
        return "Freitext — nicht prüfbar";
      case "kein_techniker":
        return "Kein Auftragstechniker — nicht prüfbar";
      case "kein_termin":
        return "Kein Auftragstermin — nicht prüfbar";
      case "versand_prioritaet":
        return "Versand-Priorität — kein Verbrauch vor Ort";
      case "staging_vorlage":
        return "Staging-Material — Verfügbarkeit wird noch nicht berechnet";
      default:
        return "nicht prüfbar";
    }
  }

  protected label(req: OperationRequirement): string {
    return req.article_template_description || req.free_text || "Unbekannt";
  }

  // --- Formular ---

  protected openAdd(): void {
    this.resetForm();
    this.editingRequirement.set(null);
    this.showModal.set(true);
  }

  protected openEdit(req: OperationRequirement): void {
    this.resetForm();
    this.editingRequirement.set(req);
    this.formQuantity = req.quantity;
    if (req.article_template_id) {
      this.selectedTemplate.set({
        id: req.article_template_id,
        description: req.article_template_description || "",
      } as ArticleTemplate);
      this.formSearchText = req.article_template_description || "";
    } else {
      this.formSearchText = req.free_text || "";
    }
    this.showModal.set(true);
  }

  protected closeModal(): void {
    this.showModal.set(false);
    this.editingRequirement.set(null);
    this.resetForm();
  }

  protected canSave(): boolean {
    return this.formQuantity >= 1 && (this.selectedTemplate() !== null || this.formSearchText.trim().length > 0);
  }

  protected async saveRequirement(): Promise<void> {
    const template = this.selectedTemplate();
    // Das jeweils andere Feld explizit auf null setzen (nicht weglassen) — beim
    // Wechsel Freitext ↔ Artikelvorlage bliebe der alte Wert sonst stehen.
    const data: Partial<OperationRequirement> & { free_text: string | null; article_template_id: number | null } = {
      action_type: "BEDARF",
      quantity: this.formQuantity,
      article_template_id: template ? template.id : null,
      free_text: template ? null : this.formSearchText.trim(),
    };

    const editing = this.editingRequirement();
    const result = editing
      ? await this.reqService.updateRequirement(editing.id, data)
      : await this.reqService.createRequirement(this.operationId, data);
    if (!result) return;

    this.closeModal();
    await this.reload();
  }

  protected async deleteRequirement(req: OperationRequirement): Promise<void> {
    if (!confirm(`"${req.quantity}x ${this.label(req)}" wirklich löschen?`)) return;
    if (await this.reqService.deleteRequirement(req.id)) await this.reload();
  }

  protected onSearchInput(): void {
    this.selectedTemplate.set(null);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);

    if (this.formSearchText.trim().length < 2) {
      this.searchResults.set([]);
      return;
    }
    this.searchDebounce = setTimeout(() => this.performSearch(), 300);
  }

  protected selectTemplate(template: ArticleTemplate): void {
    this.selectedTemplate.set(template);
    this.formSearchText = template.description;
    this.searchResults.set([]);
  }

  protected onSearchBlur(): void {
    this.searchResults.set([]);
  }

  private performSearch(): void {
    const query = this.formSearchText.toLowerCase();
    const results = this.allTemplates().filter(
      (t) => t.description?.toLowerCase().includes(query) || t.part_nr?.toLowerCase().includes(query),
    );
    this.searchResults.set(results.slice(0, 15));
  }

  private async loadTemplates(): Promise<void> {
    const all = await firstValueFrom(this.articleService.loadArticleTemplates());
    this.allTemplates.set(all || []);
  }

  private resetForm(): void {
    this.formQuantity = 1;
    this.formSearchText = "";
    this.selectedTemplate.set(null);
    this.searchResults.set([]);
  }
}
